// GeexBlobPercent — Geex frame 05 (workspace/design-refs/geex/
// 05-widgets-shopping-blobs.png, "Monthly Expenses" right phone): organic
// pastel blobs (CSS asymmetric border-radius, no imagery) sized by share,
// giant % centered inside in solid ink; legend dots + values at foot. Use
// SPARINGLY — one per surface (brief: "playful share views... use
// sparingly").
//
// items: [{ label, value, amount?, hue? }] — value is the percent share
// (drives blob diameter via sqrt, so AREA reads proportional to share);
// amount is an optional pre-formatted footer figure. Per-item `hue`
// overrides the rotation for that one blob/dot.
// ink: base foreground (default 'title'). hue: optional single rotation
// seed — when set, blobs/dots cycle a small pastel family (`hue+30`-class
// tints) instead of falling back to plain ink-alpha tones.

// FA204 — the shape/rotation tables are used only by this component's
// descendant handlers (Field.children/Legend.children below); carried via
// `scope` so frank serialization keeps them (a bare module-scope const can
// silently read as undefined once a component is frank.toJSON'd —
// el.scope.<name> resolves from any descendant handler, same as
// MarketplaceKindRail's `scope: { hydrate, cardComponent }`).
export const GeexBlobPercent = {
  scope: {
    // Helpers TOO must live here, not at module scope — the tables alone
    // being scoped wasn't enough: the module-scope _tone/_dotTone fns read
    // as undefined post-frank.toJSON and both children factories died.
    blobTone: (ink, hue, itemHue, i, rotation) => {
      if (itemHue) return `${itemHue}+30`
      if (hue) return `${rotation[i % rotation.length]}+30`
      return `${ink}.15`
    },
    dotTone: (ink, hue, itemHue, i, rotation) => {
      if (itemHue) return `${itemHue}+30`
      if (hue) return `${rotation[i % rotation.length]}+30`
      return `${ink}.35`
    },
    BLOB_SHAPES: [
      '42% 58% 65% 35% / 55% 45% 60% 40%',
      '60% 40% 45% 55% / 40% 60% 45% 55%',
      '38% 62% 55% 45% / 60% 40% 65% 35%',
      '55% 45% 40% 60% / 45% 60% 40% 55%'
    ],
    PASTEL_ROTATION: ['pink', 'phosphorus', 'cerulean', 'yellow']
  },
  flow: 'y',
  gap: 'C',
  items: [],
  ink: 'title',

  Field: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: 'C',
    childrenAs: 'state',
    children: (el, s) => {
      const parent = el.parent
      const items = parent.items || []
      const ink = parent.ink || 'title'
      const hue = parent.hue
      const shapes = el.scope.BLOB_SHAPES
      const rotation = el.scope.PASTEL_ROTATION
      return items.map((it, i) => ({
        key: it.label || `blob${i}`,
        value: it.value,
        size: Math.max(Math.sqrt(Math.max(Number(it.value) || 0, 4)) * 14, 64),
        shape: shapes[i % shapes.length],
        tone: el.scope.blobTone(ink, hue, it.hue, i, rotation),
        ink
      }))
    },
    childExtends: {
      flow: 'y',
      align: 'center center',
      flexShrink: '0',
      width: (el, s) => `${s.size}px`,
      height: (el, s) => `${s.size}px`,
      borderRadius: (el, s) => s.shape,
      background: (el, s) => s.tone,

      Value: {
        tag: 'span',
        fontSize: 'G',
        fontWeight: '700',
        fontFamily: 'Code',
        color: (el, s) => s.ink,
        text: (el, s) => `${s.value ?? 0}%`
      }
    }
  },

  Legend: {
    tag: 'div',
    flow: 'x',
    flexWrap: 'wrap',
    gap: 'C',
    hide: (el) => !(el.parent.items || []).length,
    childrenAs: 'state',
    children: (el, s) => {
      const parent = el.parent
      const items = parent.items || []
      const ink = parent.ink || 'title'
      const hue = parent.hue
      const rotation = el.scope.PASTEL_ROTATION
      return items.map((it, i) => ({
        key: it.label || `leg${i}`,
        label: it.label || '',
        amount: it.amount || '',
        dot: el.scope.dotTone(ink, hue, it.hue, i, rotation),
        ink
      }))
    },
    childExtends: {
      flow: 'y',
      gap: 'V',
      Top: {
        flow: 'x',
        align: 'center start',
        gap: 'V',
        Dot: {
          width: 'V1',
          height: 'V1',
          round: '50%',
          flexShrink: '0',
          background: (el, s) => s.dot
        },
        Label: {
          tag: 'span',
          fontSize: 'Z',
          opacity: '0.55',
          color: (el, s) => s.ink,
          // `items[].label` is a KEY-IN-STATE carrier: a consumer may hand this
          // chart a bundle key (workspace/pages/admin/usage.js feeds
          // `admin.usage.attr.systemEmpty`). Resolved HERE, at the render site —
          // `translate()` returns its input unchanged when nothing resolves, so a
          // tenant-supplied label passes through untouched.
          text: (el, s) => el.call('polyglot', s.label || '')
        }
      },
      Amount: {
        tag: 'span',
        fontSize: 'Z',
        fontWeight: '700',
        fontFamily: 'Code',
        color: (el, s) => s.ink,
        hide: (el, s) => !s.amount,
        text: (el, s) => s.amount
      }
    }
  }
}
